import type { Ref } from 'react';
import { useCallback, useEffect, useImperativeHandle, useRef } from 'react';

import { cn } from '@/lib/utils';

export type BrushCanvasHistory = { canUndo: boolean; hasMask: boolean };

export type BrushCanvasHandle = {
  undo: () => void;
  clear: () => void;
  exportMaskPng: () => Promise<Blob | null>;
};

type Tool = 'brush' | 'eraser';

type Point = { nx: number; ny: number };

type Stroke = {
  tool: Tool;
  brushSize: number;
  points: Point[];
};

type Props = {
  handleRef?: Ref<BrushCanvasHandle>;
  enabled: boolean;
  brushSize: number;
  brushColor: string;
  tool?: Tool;
  className?: string;
  layoutKey?: string | number;
  onHistoryChange?: (state: BrushCanvasHistory) => void;
  onDrawingStart?: () => void;
};

const getTargetElement = (canvas: HTMLCanvasElement | null) => {
  const parent = canvas?.parentElement;
  if (!parent) return null;

  return parent.querySelector('img') ?? parent;
};

export const BrushCanvas = ({
  handleRef,
  enabled,
  brushSize,
  brushColor,
  tool = 'brush',
  className,
  layoutKey,
  onHistoryChange,
  onDrawingStart,
}: Props) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);

  const dprRef = useRef(1);
  const sizeRef = useRef({ w: 1, h: 1 });

  const strokesRef = useRef<Stroke[]>([]);
  const activeStrokeRef = useRef<Stroke | null>(null);
  const drawingRef = useRef(false);
  const livePathOpenRef = useRef(false);

  const emitHistory = useCallback(() => {
    const hasMask = strokesRef.current.length > 0;
    onHistoryChange?.({
      canUndo: hasMask,
      hasMask,
    });
  }, [onHistoryChange]);

  const applyStrokeStyle = useCallback(
    (ctx: CanvasRenderingContext2D, strokeTool: Tool, size: number, color: string) => {
      const isEraser = strokeTool === 'eraser';

      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.lineWidth = size;
      ctx.globalAlpha = 1;
      ctx.strokeStyle = isEraser ? 'rgba(0,0,0,1)' : color;
      ctx.fillStyle = isEraser ? 'rgba(0,0,0,1)' : color;
      ctx.globalCompositeOperation = isEraser ? 'destination-out' : 'source-over';
    },
    []
  );

  const drawStroke = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      stroke: Stroke,
      w: number,
      h: number,
      color: string,
      compositeOperation: GlobalCompositeOperation = stroke.tool === 'eraser'
        ? 'destination-out'
        : 'source-over'
    ) => {
      const pts = stroke.points;
      if (pts.length === 0) return;
      ctx.save();
      applyStrokeStyle(ctx, stroke.tool, stroke.brushSize, color);
      ctx.globalCompositeOperation = compositeOperation;

      const first = pts[0];
      const x0 = first.nx * w;
      const y0 = first.ny * h;

      if (pts.length === 1) {
        ctx.beginPath();
        ctx.arc(x0, y0, stroke.brushSize / 2, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
        return;
      }

      ctx.beginPath();
      ctx.moveTo(x0, y0);

      for (let i = 1; i < pts.length; i += 1) {
        ctx.lineTo(pts[i].nx * w, pts[i].ny * h);
      }

      ctx.stroke();
      ctx.restore();
    },
    [applyStrokeStyle]
  );

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (!canvas || !ctx) return;

    const { w, h } = sizeRef.current;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.setTransform(dprRef.current, 0, 0, dprRef.current, 0, 0);

    for (const stroke of strokesRef.current) {
      drawStroke(ctx, stroke, w, h, brushColor);
    }
  }, [brushColor, drawStroke]);

  const syncSize = useCallback(() => {
    const canvas = canvasRef.current;
    const parent = canvas?.parentElement;
    const target = getTargetElement(canvas);
    if (!canvas || !parent || !target) return;

    const rect = target.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) return;

    const parentRect = parent.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    dprRef.current = dpr;
    sizeRef.current = {
      w: rect.width,
      h: rect.height,
    };

    const nextWidth = Math.max(1, Math.floor(rect.width * dpr));
    const nextHeight = Math.max(1, Math.floor(rect.height * dpr));

    canvas.style.left = `${rect.left - parentRect.left}px`;
    canvas.style.top = `${rect.top - parentRect.top}px`;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;

    if (canvas.width === nextWidth && canvas.height === nextHeight && ctxRef.current) {
      redraw();
      return;
    }

    canvas.width = nextWidth;
    canvas.height = nextHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctxRef.current = ctx;

    redraw();
    emitHistory();
  }, [emitHistory, redraw]);

  useEffect(() => {
    syncSize();

    const canvas = canvasRef.current;
    const parent = canvas?.parentElement;
    const target = getTargetElement(canvas);
    if (!canvas || !parent) return;

    const ro = new ResizeObserver(() => {
      requestAnimationFrame(syncSize);
    });

    ro.observe(parent);
    if (target instanceof HTMLImageElement) {
      ro.observe(target);
      target.addEventListener('load', syncSize);
    }

    window.addEventListener('resize', syncSize);
    window.visualViewport?.addEventListener('resize', syncSize);
    window.visualViewport?.addEventListener('scroll', syncSize);

    return () => {
      ro.disconnect();
      if (target instanceof HTMLImageElement) {
        target.removeEventListener('load', syncSize);
      }
      window.removeEventListener('resize', syncSize);
      window.visualViewport?.removeEventListener('resize', syncSize);
      window.visualViewport?.removeEventListener('scroll', syncSize);
    };
  }, [syncSize]);

  useEffect(() => {
    if (layoutKey === undefined) return;

    requestAnimationFrame(syncSize);
  }, [layoutKey, syncSize]);

  const getPoint = useCallback((e: PointerEvent) => {
    const canvas = canvasRef.current;
    const target = getTargetElement(canvas);
    if (!canvas || !target) return null;

    const rect = target.getBoundingClientRect();

    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }, []);

  const pushPoint = useCallback((x: number, y: number) => {
    const stroke = activeStrokeRef.current;
    const { w, h } = sizeRef.current;

    if (!stroke || !w || !h) return;

    stroke.points.push({
      nx: x / w,
      ny: y / h,
    });
  }, []);

  const drawSegment = useCallback(
    (x: number, y: number) => {
      const ctx = ctxRef.current;
      const stroke = activeStrokeRef.current;
      if (!ctx || !stroke) return;

      const { w, h } = sizeRef.current;
      const prev = stroke.points.at(-1);
      ctx.save();
      applyStrokeStyle(ctx, stroke.tool, stroke.brushSize, brushColor);

      if (!prev) {
        // Start a single continuous path for the whole stroke so we don't get
        // "stamp-like" circles from separate segments.
        ctx.beginPath();
        ctx.arc(x, y, stroke.brushSize / 2, 0, Math.PI * 2);
        ctx.fill();

        ctx.beginPath();
        ctx.moveTo(x, y);
        livePathOpenRef.current = true;
      } else {
        if (!livePathOpenRef.current) {
          ctx.beginPath();
          ctx.moveTo(prev.nx * w, prev.ny * h);
          livePathOpenRef.current = true;
        }
        ctx.lineTo(x, y);
        ctx.stroke();
      }

      ctx.restore();
      pushPoint(x, y);
    },
    [applyStrokeStyle, brushColor, pushPoint]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const onPointerDown = (e: PointerEvent) => {
      if (!enabled) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;

      e.preventDefault();

      canvas.setPointerCapture(e.pointerId);
      drawingRef.current = true;
      livePathOpenRef.current = false;
      activeStrokeRef.current = { tool, brushSize, points: [] };

      const point = getPoint(e);
      if (point) {
        onDrawingStart?.();
        drawSegment(point.x, point.y);
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!enabled || !drawingRef.current) return;

      e.preventDefault();

      const point = getPoint(e);
      if (point) drawSegment(point.x, point.y);
    };

    const stopDrawing = (e: PointerEvent) => {
      if (!drawingRef.current) return;

      drawingRef.current = false;
      livePathOpenRef.current = false;

      if (canvas.hasPointerCapture(e.pointerId)) {
        canvas.releasePointerCapture(e.pointerId);
      }

      const stroke = activeStrokeRef.current;
      activeStrokeRef.current = null;

      if (stroke?.points.length) {
        strokesRef.current.push(stroke);
        emitHistory();
      }
    };

    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', stopDrawing);
    canvas.addEventListener('pointercancel', stopDrawing);
    canvas.addEventListener('pointerleave', stopDrawing);

    return () => {
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', stopDrawing);
      canvas.removeEventListener('pointercancel', stopDrawing);
      canvas.removeEventListener('pointerleave', stopDrawing);
    };
  }, [brushSize, drawSegment, emitHistory, enabled, getPoint, onDrawingStart, tool]);

  useImperativeHandle(handleRef, () => ({
    undo: () => {
      if (strokesRef.current.length === 0) return;

      strokesRef.current.pop();
      activeStrokeRef.current = null;
      drawingRef.current = false;
      livePathOpenRef.current = false;
      redraw();
      emitHistory();
    },

    clear: () => {
      strokesRef.current = [];
      activeStrokeRef.current = null;
      drawingRef.current = false;
      livePathOpenRef.current = false;
      redraw();
      emitHistory();
    },

    exportMaskPng: async () => {
      const canvas = canvasRef.current;
      if (!canvas || strokesRef.current.length === 0) return null;

      const offscreen = document.createElement('canvas');
      offscreen.width = canvas.width;
      offscreen.height = canvas.height;

      const ctx = offscreen.getContext('2d');
      if (!ctx) return null;

      const dpr = dprRef.current;
      const { w, h } = sizeRef.current;

      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, offscreen.width, offscreen.height);

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      for (const stroke of strokesRef.current) {
        if (stroke.tool === 'eraser') {
          drawStroke(ctx, stroke, w, h, '#000', 'source-over');
        } else {
          drawStroke(ctx, stroke, w, h, '#fff', 'source-over');
        }
      }

      return await new Promise<Blob | null>((resolve) => {
        offscreen.toBlob((blob) => resolve(blob), 'image/png');
      });
    },
  }));

  return (
    <canvas
      ref={canvasRef}
      className={cn(
        'absolute touch-none',
        enabled ? 'pointer-events-auto' : 'pointer-events-none',
        className
      )}
    />
  );
};
